import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useNavSize } from '@frontend/responsiveness';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Heading, SkeletonLoader, Truncated } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { RoutesTableTab } from '../routes-list-table/routes-tabs';

export interface CallRouteDetailsHeaderProps {
  callRouteName: string;
  isLoading: boolean;
  isDeviceExtensionRoute: boolean;
  redirectCallRouteId?: string;
  redirectCallRouteName?: string;
  redirectNodeId?: string;
  onRenameClick: () => void;
  onDeleteClick: () => void;
}

interface BreadcrumbItem {
  label: string;
  to?: SettingsRoutes;
  onClick?: () => void;
}

/**
 * Header configuration for the Call Route Details page.
 * Returns the breadcrumbs, heading, and action button(s) to be used in the Page.Header.
 */
export const CallRouteDetailsHeader = ({
  callRouteName,
  isLoading,
  isDeviceExtensionRoute,
  redirectCallRouteId,
  redirectCallRouteName,
  redirectNodeId,
  onRenameClick,
  onDeleteClick,
}: CallRouteDetailsHeaderProps) => {
  const { t } = useTranslation('phone');
  const { navigate: settingsNavigate } = useSettingsNavigate<{
    contextNodeId?: string;
    contextPrimitiveId?: string;
  }>();
  const navSize = useNavSize();
  const isSmallerThanDesktop = navSize.isLt('large');

  // Note: We import Page from @frontend/page in the parent component
  // This component returns the content for Page.Header
  const breadcrumbs: BreadcrumbItem[] = redirectCallRouteId
    ? [
        { label: t('Phone'), to: '/phone/main' as SettingsRoutes },
        { label: t('Call Routes'), to: '/phone/call-routes' as SettingsRoutes },
        {
          label: redirectCallRouteName ?? '',
          onClick: () =>
            settingsNavigate({
              to: '/phone/call-routes/:id',
              params: { id: redirectCallRouteId },
              context: { contextNodeId: redirectNodeId },
            }),
        },
        { label: callRouteName },
      ]
    : [
        { label: t('Phone'), to: '/phone/main' as SettingsRoutes },
        {
          label: t('Call Routes'),
          onClick: () =>
            settingsNavigate({
              to: '/phone/call-routes',
              search: {
                type: isDeviceExtensionRoute ? RoutesTableTab.DEVICE_ROUTES : RoutesTableTab.CALL_ROUTES,
              },
            }),
        },
        { label: callRouteName },
      ];

  return {
    breadcrumbs,

    heading: isLoading ? (
      <SkeletonLoader width={300} />
    ) : (
      <div
        css={css`
          container-type: inline-size;
        `}
      >
        <div
          css={css`
            max-width: 700px;
            display: inline-flex;
            align-items: end;

            @container (max-width: 700px) {
              max-width: 400px;
            }
            @container (max-width: 400px) {
              max-width: 300px;
            }
          `}
        >
          <div
            css={css`
              min-width: 0;
            `}
          >
            <Heading level={isSmallerThanDesktop ? 2 : 1} as={Truncated}>
              {callRouteName}
            </Heading>{' '}
          </div>
          {!isDeviceExtensionRoute && (
            <Button
              size='small'
              variant='secondary'
              iconName='edit-small'
              trackingId={trackingId({
                context: 'setting',
                feature: 'call-route',
                details: 'edit-call-route-name',
              })}
              css={{
                '.button-icon': {
                  color: theme.colors.neutral60,
                },
              }}
              onClick={onRenameClick}
            />
          )}
        </div>
      </div>
    ),

    action: isLoading ? (
      <SkeletonLoader height={40} width={160} />
    ) : (
      <>
        {!isDeviceExtensionRoute && (
          <Button
            variant='secondary'
            size='large'
            destructive
            disabled={isLoading}
            onClick={onDeleteClick}
            trackingId={trackingId({
              context: 'setting',
              feature: 'call-route',
              details: 'delete-call-route-btn',
            })}
          >
            {t('Delete Call Route')}
          </Button>
        )}
      </>
    ),
  };
};
